// deps
import classNames from "classnames";
import React, { memo, ReactElement, useMemo } from "react";

// internal-deps
import { isEmpty, WalletApiFetchState } from "@swiggy-private/payment-core";
import { VALID_WALLET_TYPES } from "@swiggy-private/payment-types";

// helpers
import { getPaymentMethodCode, isAmazonWallet, getWalletAriaLabel } from "@payment-ui/helpers/Wallet";
import {
    getPaymentMethodPromoMsg,
    getPaymentMethodIconUrl,
    getPaymentMethodDisableMsg,
    canEnableWalletMethod,
    getPaymentMethodDisabledMsg,
    getPaymentMethodDiscount,
    getPaymentAmount,
    getAmazonPayDelinkedMessage,
} from "@payment-ui/helpers/Payment";

// hooks
import { useWalletActions, useWalletOnLoad } from "./hooks";

// interfaces
import { IWalletPaymentGroup, WalletPaymentMethodData } from "@payment-ui/interfaces/Wallet";
import { PaymentMethodProps } from "@payment-ui/interfaces/Payment";
import { User } from "@swiggy-private/payment-core";

// components
import Alert from "@payment-ui/widgets/alert/Alert";
import PaymentMethodIcon from "@payment-ui/widgets/payment-method-icon/PaymentMethodIcon";
import PaymentMethodName from "@payment-ui/widgets/payment-method-name/PaymentMethodName";
import PayButton from "./components/PayButton";
import WalletBalance from "./components/WalletBalance";
import { AlertMsg } from "./components/AlertMsg";

// payment-testids
import { getWalletTestIds } from "@swiggy-private/payment-test-ids";
import styles from "./Wallet.v4.scss";

interface WalletProps extends PaymentMethodProps {
    methodData: WalletPaymentMethodData;
    paymentActions: IWalletPaymentGroup["paymentActions"];
    user?: User;
    paymentAmount: string;
}

const WALLET_PAYMENT_ICON_WIDTH = 32;

function WalletPaymentMethod({ user, ...props }: WalletProps): ReactElement | null {
    const { getWalletBalance, getAmazonPaySdkStatus, getPhonepeSdkStatus, onLoad } = props.paymentActions;

    const [fetchState, walletData] = useMemo(
        () => getWalletBalance(props.methodData),
        [getWalletBalance, props.methodData],
    );
    const walletBalanceLoading = fetchState === WalletApiFetchState.LOADING;

    const paymentCode = useMemo(() => getPaymentMethodCode(props.methodData), [props.methodData]);
    const walletTestIds = useMemo(() => getWalletTestIds(paymentCode), [paymentCode]);
    const { state: amazonPaySdkStatus, sdkFetchInProgress } = getAmazonPaySdkStatus();
    const { isPhonepeSdkAvailable } = getPhonepeSdkStatus();
    const _isAmazonWallet = isAmazonWallet(props.methodData);
    const userMobileNumber = user?.mobile ?? "";

    const enabled = useMemo(
        () => canEnableWalletMethod(walletData, props.methodData, props.isPaymentGroupEnabled),
        [props.isPaymentGroupEnabled, props.methodData, walletData],
    );

    const infoMessage = useMemo(() => {
        const { isNewSdkAvailable, linked, wasPreviouslyLinkedUsingSDK } = amazonPaySdkStatus ?? {};
        if (_isAmazonWallet && isNewSdkAvailable && !linked && wasPreviouslyLinkedUsingSDK === true) {
            // for amazon, if wallet was linked before app update
            // then over ride promo message with delinked message
            return getAmazonPayDelinkedMessage(props.methodData);
        }
        return getPaymentMethodPromoMsg(props.methodData);
    }, [_isAmazonWallet, amazonPaySdkStatus, props.methodData]);

    // Special handling to disable add money when paytm balance is insufficient
    const isPayTmMethodWithLessBalance = useMemo(() => {
        const isPayTmMethod = props.methodData.name?.toUpperCase() === VALID_WALLET_TYPES.PAYTM;
        return isPayTmMethod && walletData.linked && !walletData.isWalletBalanceSufficent;
    }, [props.methodData.name, walletData.isWalletBalanceSufficent, walletData.linked]);

    const { onClick, onPayClick, onAddMoneyClick, onAddMoneyImpressionEvent } = useWalletActions({
        methodData: props.methodData,
        actions: props.paymentActions,
        enabled,
        isPayTmMethodWithLessBalance,
        fetchState,
        walletData,
        isAmazonWallet: _isAmazonWallet,
        sdkFetchInProgress,
        amazonPaySdkStatus,
        paymentCode,
        isPhonepeSdkAvailable,
        userMobile: user?.mobile,
    });

    const containerClass = classNames(styles.paymentMethod, {
        [styles.paymentMethodDisabled]: !enabled || isPayTmMethodWithLessBalance,
    });

    const paymentMethodIcon = useMemo(
        (): ReactElement => (
            <PaymentMethodIcon
                src={getPaymentMethodIconUrl(props.methodData)}
                alt={props.methodData.displayName}
                width={WALLET_PAYMENT_ICON_WIDTH}
                data-testid={walletTestIds.ICON}
            />
        ),
        [props.methodData, walletTestIds],
    );

    // pay section can be shown only when wallet is selected and also linked
    const canShowPaySection = useMemo((): boolean => {
        // Not showing pay section for paytm wallet with less balance as add money is not allowed
        if (!props.selected || isPayTmMethodWithLessBalance) {
            return false;
        }
        if (!_isAmazonWallet) {
            return walletData.linked;
        }
        // in case of amazonpay wallet
        if (sdkFetchInProgress) {
            // cannot show pay section when amazon sdk fetch is in progress
            return false;
        }
        // linking wallet will be applicable only in amazon sdk flow and not in web redirect flow.
        // So we make linked value true to allow user to proceed with payment in web redirect flow.
        return amazonPaySdkStatus != null && amazonPaySdkStatus.available ? amazonPaySdkStatus.linked : true;
    }, [
        props.selected,
        isPayTmMethodWithLessBalance,
        _isAmazonWallet,
        sdkFetchInProgress,
        amazonPaySdkStatus,
        walletData.linked,
    ]);

    const walletAriaLabel = useMemo(
        () =>
            getWalletAriaLabel({
                paymentMethodData: props.methodData,
                walletData,
                isSelected: props.selected,
                isEnabled: enabled,
            }),
        [enabled, props.methodData, props.selected, walletData],
    );

    const discount = getPaymentMethodDiscount(props.methodData);
    const paymentAmount = getPaymentAmount(props.paymentAmount, props.methodData);

    useWalletOnLoad({
        methodData: props.methodData,
        onLoad,
        enabled,
        isAmazonWallet: _isAmazonWallet,
        walletData,
        amazonPaySdkStatus,
        paymentAmount,
    });

    return (
        <div className={containerClass} onClick={onClick} data-testid={walletTestIds.CONTAINER}>
            <div className={styles.paymentMethodContainer}>
                {paymentMethodIcon}

                <PaymentMethodName
                    displayName={props.methodData.displayName}
                    promoMsg={infoMessage}
                    enabled={enabled}
                    ariaLabel={walletAriaLabel}
                    data-testid={walletTestIds.NAME}
                    discount={discount}
                >
                    {isEmpty(discount) && (
                        <>
                            {enabled ? (
                                !_isAmazonWallet &&
                                walletData.linked && (
                                    <AlertMsg
                                        state={fetchState}
                                        walletData={walletData}
                                        userMobileNumber={userMobileNumber}
                                        isPayTmMethodWithLessBalance={isPayTmMethodWithLessBalance}
                                    />
                                )
                            ) : (
                                <Alert
                                    className={styles.paymentMethodDisable}
                                    message={
                                        walletData.paymentMethodDisabled === true
                                            ? getPaymentMethodDisabledMsg(props.methodData)
                                            : getPaymentMethodDisableMsg(props.methodData)
                                    }
                                    data-testid={walletTestIds.DISABLE_MESSAGE}
                                />
                            )}
                        </>
                    )}
                </PaymentMethodName>

                {enabled && user && (
                    <WalletBalance
                        walletBalanceLoading={walletBalanceLoading}
                        selected={props.selected}
                        methodData={props.methodData}
                        actions={props.paymentActions}
                        linkStatus={walletData.linkStatus}
                        hasLessBalance={!walletData.isWalletBalanceSufficent}
                        balance={walletData.balance}
                        walletTestIds={walletTestIds}
                    />
                )}
            </div>

            {enabled && canShowPaySection && (
                <PayButton
                    onPay={onPayClick}
                    onAddMoney={onAddMoneyClick}
                    paymentAmount={paymentAmount}
                    walletBalanceLoading={walletBalanceLoading}
                    hasSufficientBalance={walletData.isWalletBalanceSufficent}
                    methodData={props.methodData}
                    additionalBalanceNeeded={walletData.additionalBalanceRequired}
                    addMoneyImpressionEvent={onAddMoneyImpressionEvent}
                    walletTestIds={walletTestIds}
                />
            )}
        </div>
    );
}

export default memo(WalletPaymentMethod);
